import {
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowResetRegular, DismissRegular } from '@fluentui/react-icons';
import { formatDate } from '@moko/utils';
import { DEFAULT_TOGGLE_LABELS, OPERATOR_CONFIG } from './filter.const';
import { FilterType, type IActiveFilter } from './filter.types';

const useStyles = makeStyles({
  label: {
    display: 'inline',
  },
  operator: {
    fontWeight: tokens.fontWeightSemibold,
  },
});

interface FilterTagProps {
  filter: IActiveFilter;
  onClick?: () => void;
  /** Remove the chip entirely. Ignored when `onReset` is provided. */
  onRemove?: () => void;
  /** Reset the value (default-filter chips: chip stays, value clears). */
  onReset?: () => void;
}

export const FilterTag = ({
  filter,
  onClick,
  onRemove,
  onReset,
}: FilterTagProps) => {
  const styles = useStyles();

  const formatDisplayValue = (): string => {
    const { value } = filter;
    const options = filter.column.options ?? [];

    // Array value (In operator)
    if (Array.isArray(value)) {
      if (value.length === 0) return '';
      const labels = value.map(
        (v) => options.find((o) => o.value === v)?.label ?? v
      );
      return labels.join(', ');
    }

    // Date value
    if (filter.column.type === FilterType.Date) {
      return formatDate(value);
    }

    // Single select value (Eq/NEq with options)
    if (options.length > 0) {
      return options.find((o) => o.value === value)?.label ?? value;
    }

    return value;
  };

  const operatorConfig = OPERATOR_CONFIG[filter.operator];
  const displayValue = formatDisplayValue();
  const isToggle = Boolean(filter.column.toggleOptions);
  const toggleLabel = isToggle
    ? filter.column.toggleOptions?.find((o) => o.value === filter.value)
        ?.label ??
      (filter.value === filter.column.toggleOptions?.[0].value
        ? DEFAULT_TOGGLE_LABELS.on
        : DEFAULT_TOGGLE_LABELS.off)
    : undefined;

  const isUnfilledDefault =
    !!filter.column.isDefault &&
    operatorConfig.requiresValue &&
    !isToggle &&
    !displayValue;

  return (
    <InteractionTag size="small" shape="circular" appearance="brand">
      <InteractionTagPrimary hasSecondaryAction onClick={onClick}>
        <span className={styles.label}>{filter.column.label}</span>
        {!isUnfilledDefault && (
          <span className={styles.operator}> {operatorConfig.label}</span>
        )}
        {isToggle && <span className={styles.label}> {toggleLabel}</span>}
        {!isToggle && operatorConfig.requiresValue && displayValue && (
          <span className={styles.label}> {displayValue}</span>
        )}
      </InteractionTagPrimary>
      {onReset ? (
        <InteractionTagSecondary aria-label="reset" onClick={onReset}>
          <ArrowResetRegular />
        </InteractionTagSecondary>
      ) : (
        <InteractionTagSecondary aria-label="remove" onClick={onRemove}>
          <DismissRegular />
        </InteractionTagSecondary>
      )}
    </InteractionTag>
  );
};
